import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, rateKeysForGrade } from "@/lib/api";
import TipLetter from "@/components/TipLetter";
import { ArrowLeft, Printer } from "lucide-react";

export default function LetterView() {
  const { type, id } = useParams();
  const navigate = useNavigate();

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: group } = useQuery({
    queryKey: ["group", id],
    enabled: type === "group" && !!id,
    queryFn: async () => (await api.get(`/groups/${id}`)).data,
  });
  const { data: bus } = useQuery({
    queryKey: ["bus", id],
    enabled: type === "bus" && !!id,
    queryFn: async () => (await api.get(`/buses/${id}`)).data,
  });

  if (!settings) return <div className="p-10">Loading…</div>;

  let title = "";
  let audience = "group";
  let staffTable = [];
  let rateRows = [];
  let showDeadlines = true;
  let extraNote = null;

  const t = settings.tip_amounts || {};

  if (type === "group") {
    if (!group) return <div className="p-10">Loading letter…</div>;
    title = `Group ${group.code}`;
    audience = "group";

    const hasCIT = (group.cits || []).length > 0;

    // Build staff table with per-grade role labels
    staffTable = [];
    if (group.head_counselor)
      staffTable.push({ role: "Head Counselor", name: group.head_counselor });
    (group.assistants || []).forEach((a) => {
      const { label } = rateKeysForGrade(a.grade);
      staffTable.push({ role: label, name: a.name });
    });
    (group.cits || []).forEach((c) => {
      staffTable.push({ role: "CIT", name: c.name });
    });

    // Rate rows: include one row per unique grade that appears in this group
    rateRows = [];
    rateRows.push({ role: "Head Counselor", full: t.head_counselor_full, half: t.head_counselor_half });

    const seenKeys = new Set();
    (group.assistants || []).forEach((a) => {
      const { full, half, label } = rateKeysForGrade(a.grade);
      if (!full) return; // no grade assigned yet -> skip rate row
      if (!seenKeys.has(full)) {
        seenKeys.add(full);
        rateRows.push({ role: label, full: t[full], half: t[half] });
      }
    });

    if (hasCIT)
      rateRows.push({ role: "CIT (Counselor-In-Training)", full: t.cit_full, half: t.cit_half });
  } else if (type === "bus") {
    if (!bus) return <div className="p-10">Loading letter…</div>;
    title = `Bus #${bus.number}`;
    audience = "bus";
    staffTable = [
      { role: "Bus Driver", name: bus.driver },
      { role: "Bus Counselor", name: bus.counselor },
    ];
    rateRows = [
      { role: "Bus Driver", full: t.bus_driver_full, half: t.bus_driver_half },
      { role: "Bus Counselor", full: t.bus_counselor_full, half: t.bus_counselor_half },
    ];
  } else if (type === "cit") {
    title = "CITs";
    audience = "cit";
    const sups = settings.cit_supervisors || [];
    staffTable = sups.map((s) => ({ role: s.role || "CIT Supervisor", name: s.name }));
    rateRows = [
      { role: "CIT Supervisor", full: t.cit_supervisor_full, half: t.cit_supervisor_half },
    ];
    extraNote =
      "Each Group Tip Letter also lists the CITs assigned to that specific group.";
  } else if (type === "ampm") {
    title = "AM and PM Care";
    audience = "ampm";
    const amList = (settings.ampm_am || []).map((p) => ({ role: "AM Care", name: p.name }));
    const pmList = (settings.ampm_pm || []).map((p) => ({ role: "PM Care", name: p.name }));
    staffTable = [...amList, ...pmList];
    rateRows = [
      { role: "AM/PM Counselor", full: t.ampm_full, half: t.ampm_half },
    ];
  } else {
    return (
      <div className="p-10">
        Unknown letter type. <Link to="/">Go home</Link>
      </div>
    );
  }

  const printLetter = () => window.print();

  return (
    <div className="min-h-screen bg-[#F1E9D6] py-4 sm:py-8 px-2 sm:px-4" data-testid="letter-view">
      {/* Action bar - no print */}
      <div className="no-print max-w-4xl mx-auto mb-4 sm:mb-6 flex items-center justify-between gap-2">
        <button
          data-testid="letter-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white text-[#0B2545] font-semibold border border-[#E9DDC8] hover:border-[#0B2545] text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="tag navy hidden sm:inline-flex">Printable · Save as PDF</span>
          <button
            data-testid="letter-print"
            onClick={printLetter}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-[#0B2545] text-[#FDF6EC] font-semibold hover:bg-[#13315C] text-sm"
          >
            <Printer className="w-4 h-4" /> Print / PDF
          </button>
        </div>
      </div>

      <TipLetter
        title={title}
        settings={settings}
        audience={audience}
        staffTable={staffTable}
        rateRows={rateRows.filter((r) => r.role)}
        extraNote={extraNote}
        showDeadlines={showDeadlines}
      />
    </div>
  );
}
